/**
 * Full ERP navigation tree.
 * Mỗi module có path duy nhất dùng làm route (hoặc query param cho tab con).
 * Sidebar render đệ quy từ cây này.
 */
import {
  Home,
  Dashboard,
  ShoppingCart,
  Store,
  ShowChart,
  Factory,
  CompareArrows,
  Science,
  Warehouse,
  Storage,
  Settings,
  Person,
  AccountBalance,
  Analytics,
  Assignment,
  Assessment,
  Category,
  ContentPasteSearch,
  CurrencyExchange,
  Description,
  FactCheck,
  Groups,
  Inventory,
  LocalShipping,
  ManageAccounts,
  Paid,
  ReceiptLong,
  Rule,
  Scale,
  Security,
  Summarize,
  SwapHoriz,
} from '@mui/icons-material'
import type { SvgIconComponent } from '@mui/icons-material'

export interface NavItem {
  key: string
  label: string
  path: string
  icon?: SvgIconComponent
  /** Permission key từ settings/roles. Item bị ẩn nếu role có key này = NoGrant (2). */
  permKey?: string
  children?: NavItem[]
}

export const navigation: NavItem[] = [
  { key: 'home', label: 'Home', path: '/home', icon: Home },

  // ─── Report ────────────────────────────────────────
  {
    key: 'dashboard',
    label: 'Report',
    path: '/report',
    icon: Dashboard,
    permKey: 'position', // RT008 — Report/Commercial Position module
    children: [
      {
        // Nhóm các report (dashboard) hiện có.
        key: 'report-list',
        label: 'Dashboards',
        path: '/report/dashboards/pnl',
        icon: Assessment,
        children: [
          { key: 'report-pnl', label: 'P&L', path: '/report/dashboards/pnl', icon: Analytics },
          { key: 'report-futures-position', label: 'Futures Position', path: '/report/dashboards/futures-position', icon: ShowChart },
          { key: 'report-risk', label: 'Risk Management', path: '/report/dashboards/risk-management', icon: Security },
          { key: 'report-long-short', label: 'Long/Short', path: '/report/dashboards/long-short', icon: SwapHoriz },
          { key: 'report-inventory', label: 'Inventory', path: '/report/dashboards/inventory', icon: Inventory },
          { key: 'report-cashflows', label: 'Cash Flows', path: '/report/dashboards/cashflows', icon: Paid },
        ],
      },
      {
        // Các trang Edit Value (sẽ bổ sung dần). Thêm child mới vào đây + khai báo
        // route tương ứng trong App.tsx dưới `report/inputs/...`.
        key: 'report-inputs',
        label: 'Inputs',
        path: '/report/inputs',
        icon: Assignment,
        children: [
          { key: 'report-inputs-exposure', label: 'Exposure Limit', path: '/report/inputs/exposure', icon: Security },
          { key: 'report-inputs-cashflow', label: 'Cash Flow', path: '/report/inputs/cashflow', icon: Paid },
        ],
      },
    ],
  },

  // ─── Sales ───────────────────────────────────────────────────────
  {
    key: 'sales',
    label: 'Sales',
    path: '/sales',
    icon: Store,
    children: [
      {
        key: 'sales-contract',
        label: 'Contract',
        path: '/sales/contract',
        icon: Description,
        permKey: 'saleContract',
        children: [
          { key: 'sale-contracts', label: 'Sales Contract', path: '/sales/contract/list' },
          { key: 'consignment', label: 'Consignment', path: '/sales/contract/consignment' },
          { key: 'sales-amendment', label: 'Amendment', path: '/sales/contract/amendment' },
          { key: 'sales-ptbf-fixation', label: 'PTBF Fixation', path: '/sales/contract/ptbf-fixation' },
          { key: 'sales-contract-references', label: 'References', path: '/sales/contract/references' },
        ],
      },
      {
        key: 'sales-control',
        label: 'Control',
        path: '/sales/control',
        icon: FactCheck,
        permKey: 'saleControl',
        children: [
          { key: 'sales-contract', label: 'Sales Contract', path: '/sales/control/sales-contract' },
          { key: 'sales-allocation', label: 'Sales Allocation', path: '/sales/control/sales-allocation' },
          { key: 'delivery-order', label: 'Delivery Order', path: '/sales/control/delivery-order' },
          { key: 'receive-order', label: 'Receive Order', path: '/sales/control/receive-order' },
          { key: 'sales-invoice', label: 'Invoice', path: '/sales/control/invoice' },
          { key: 'claim', label: 'Claim', path: '/sales/control/claim' },
          { key: 'credit-note', label: 'Credit Note', path: '/sales/control/credit-note' },
          { key: 'debit-note', label: 'Debit Note', path: '/sales/control/debit-note' },
          { key: 'moving', label: 'Moving', path: '/sales/control/moving' },
          { key: 'sales-references', label: 'References', path: '/sales/control/references' },
        ],
      },
    ],
  },

  // ─── Purchase ────────────────────────────────────────────────────
  {
    key: 'purchase',
    label: 'Purchase',
    path: '/purchase',
    icon: ShoppingCart,
    children: [
      {
        key: 'purchase-contract',
        label: 'Contract',
        path: '/purchase/contract',
        icon: Assignment,
        permKey: 'purchase',
        children: [
          { key: 'pc-localP', label: 'Purchase', path: '/purchase/contract/list' },
          { key: 'pc-forwardP', label: 'Forward P', path: '/purchase/contract/forward-pu' },
          { key: 'pc-npeContract', label: 'NPE Contract', path: '/purchase/contract/npe-contract' },
          { key: 'pc-amendment', label: 'Amendment', path: '/purchase/contract/amendment' },
          { key: 'pc-ptbfFixation', label: 'PTBF Fixation', path: '/purchase/contract/ptbf-fixation' },
          { key: 'pc-npeFixation', label: 'NPE Fixation', path: '/purchase/contract/npe-fixation' },
          { key: 'pc-npeAllocation', label: 'NPE Allocation', path: '/purchase/contract/npe-allocation' },
          { key: 'pc-ptbfRolling', label: 'PTBF Rolling', path: '/purchase/contract/ptbf-rolling' },
          { key: 'pc-references', label: 'References', path: '/purchase/contract/references' },
          { key: 'pc-plans', label: 'Plans', path: '/purchase/contract/plans' },
        ],
      },
      {
        key: 'purchase-control',
        label: 'Control',
        path: '/procurement',
        icon: FactCheck,
        permKey: 'procurement',
        children: [
          { key: 'procurement-purchase', label: 'Purchase', path: '/procurement/purchase' },
          { key: 'procurement-contract-detail', label: 'Contract Detail', path: '/procurement/contract-detail' },
          { key: 'procurement-pay-data', label: 'Pay Data', path: '/procurement/pay-data' },
          { key: 'procurement-invoice', label: 'Invoice', path: '/procurement/invoice' },
          { key: 'procurement-good-allocation', label: 'Good Allocation', path: '/procurement/good-allocation' },
          { key: 'procurement-references', label: 'References', path: '/procurement/references' },
          { key: 'procurement-cvbb', label: 'Documents & Minutes', path: '/procurement/cvbb' },
        ],
      },
    ],
  },

  // ─── Futures Control ─────────────────────────────────────────────
  {
    key: 'futures',
    label: 'Futures Control',
    path: '/futures',
    icon: ShowChart,
    permKey: 'futures',
    children: [
      { key: 'futures-summary', label: 'Summary', path: '/futures/summary', icon: Summarize },
      { key: 'margin-report', label: 'Margin Report', path: '/futures/margin-report', icon: Assessment },
      { key: 'market', label: 'Market', path: '/futures/market', icon: ShowChart },
      { key: 'futures-allocation', label: 'Allocation', path: '/futures/allocation', icon: Rule },
      { key: 'futures-orders', label: 'Futures', path: '/futures/orders', icon: ReceiptLong },
      { key: 'spreads-orders', label: 'Spreads', path: '/futures/spreads', icon: SwapHoriz },
      { key: 'pucontract', label: 'Purchase', path: '/futures/pucontract', icon: Paid },
      { key: 'local-s', label: 'SA Contract', path: '/futures/sacontract', icon: Paid },
      { key: 'bank-cash-in', label: 'Cash-in', path: '/futures/bank-cash-in', icon: AccountBalance },
      { key: 'bank-statement', label: 'Bank Statement', path: '/futures/bank-statement', icon: Description },
      { key: 'adjust-lot', label: 'Adjustment', path: '/futures/adjust-lot', icon: Scale },
      { key: 'bank-cost-change', label: 'Bank cost change', path: '/futures/bank-cost-change', icon: CurrencyExchange },
    ],
  },

  // ─── Factory ─────────────────────────────────────────────────────
  {
    key: 'factory',
    label: 'Factory',
    path: '/factory',
    icon: Factory,
    children: [
      // Path = factory_code (DB column tlc_factory.factory_overview). URL drop
      // prefix `/factory/` (2026-05-28) — `factoryCode` lên thẳng top-level
      // segment. Routes enumerated trong App.tsx.
      { key: 'baoloc', label: 'Bảo Lộc', path: '/baoloc', icon: Factory, permKey: 'factoryBl' },
      { key: 'nhontrach', label: 'Nhơn Trạch', path: '/nhontrach', icon: Factory, permKey: 'factoryNt' },
      { key: 'nguyencuong', label: 'An Phước', path: '/nguyencuong', icon: Factory, permKey: 'factoryNc' },
      { key: 'nongsan', label: 'Nông Sản', path: '/nongsan', icon: Factory, permKey: 'factoryNs' },
      { key: 'pacorini', label: 'Pacorini', path: '/pacorini', icon: Factory, permKey: 'factoryPaco' },
      { key: 'factory-control', label: 'Factory Control', path: '/factory/control', icon: Summarize, permKey: 'factory' },
    ],
  },

  // ─── Matching P - S ──────────────────────────────────────────────
  // Mirror Access form `frmMatchingPSMain` (NavigationControl0) — 4 nav button visible.
  {
    key: 'matching',
    label: 'Matching P - S',
    path: '/matching',
    icon: CompareArrows,
    permKey: 'matchingPs',
    children: [
      { key: 'match-instruct', label: 'Match - Instruct', path: '/matching/match-instruct', icon: Rule },
      // Ẩn khỏi sidebar (route `/matching/stock-sales` vẫn hoạt động khi truy cập trực tiếp).
      // { key: 'stock-sales', label: 'Stock to Sales', path: '/matching/stock-sales', icon: SwapHoriz },
      { key: 's-contract', label: 'S Contract', path: '/matching/s-contract', icon: Store },
      { key: 'p-contract', label: 'P Contract', path: '/matching/p-contract', icon: ShoppingCart },
    ],
  },

  // ─── Quality ─────────────────────────────────────────────────────
  {
    key: 'quality',
    label: 'Quality',
    path: '/quality',
    icon: Science,
    permKey: 'quality',
    children: [
      { key: 'sampling', label: 'Overview', path: '/quality/overview', icon: Science },
      { key: 'sampling-data', label: 'Sample Data', path: '/quality/sample-data', icon: FactCheck },
      { key: 'nestle-sample', label: 'Nestle Sample', path: '/quality/nestle-sample', icon: LocalShipping },
    ],
  },

  // ─── Virtual Warehouse ───────────────────────────────────────────
  {
    key: 'virtual-warehouse',
    label: 'Virtual Warehouse',
    path: '/virtual-warehouse',
    icon: Warehouse,
    permKey: 'virtualWarehouse',
    children: [
      { key: 'vw-received', label: 'Received', path: '/virtual-warehouse/received', icon: Inventory },
      { key: 'quality-discount', label: 'Quality Discount', path: '/virtual-warehouse/quality-discount', icon: Science },
      { key: 'delivery-note-print', label: 'Delivery Note Print', path: '/virtual-warehouse/delivery-note-print', icon: LocalShipping },
      { key: 'receive-note-print', label: 'Receive Note Print', path: '/virtual-warehouse/receive-note-print', icon: ReceiptLong },
    ],
  },


  // ─── Master Data ─────────────────────────────────────────────────
  // Same 3-level sidebar shape as `Sales > Contract / Control > tabs`.
  // 1:1 with Access `frmMdMain` (cbMDList) + sub-form nav buttons:
  //   - Futures Control → frmMdMainModFuture (Calendar / Month / Holiday)
  //   - Item & Valuation → frmMdMainModItems (CP List / Items and Valuation / Other List)
  // Deepest level (Vendor / CP Bank / License / ... etc., from each
  // sub-form's cbPreferencesList) stays inside the page as a "Go to Page"
  // Select dropdown.
  {
    key: 'master-data',
    label: 'Master Data',
    path: '/master-data',
    icon: Storage,
    permKey: 'masterData',
    children: [
      {
        key: 'md-futures-control',
        label: 'Futures Control',
        path: '/master-data/futures-control',
        children: [
          { key: 'md-fc-calendar', label: 'Calendar', path: '/master-data/futures-control/calendar' },
          { key: 'md-fc-month', label: 'Month', path: '/master-data/futures-control/month' },
          { key: 'md-fc-holiday', label: 'Holiday', path: '/master-data/futures-control/holiday' },
        ],
      },
      {
        key: 'md-items',
        label: 'Item & Valuation',
        path: '/master-data/items',
        children: [
          { key: 'md-items-cp-list', label: 'CP List', path: '/master-data/items/cp-list' },
          { key: 'md-items-valuation', label: 'Items and Valuation', path: '/master-data/items/items-valuation' },
          { key: 'md-items-other', label: 'Other List', path: '/master-data/items/other' },
        ],
      },
    ],
  },

  // ─── Settings ────────────────────────────────────────────────────
  {
    key: 'settings',
    label: 'Settings',
    path: '/settings',
    icon: Settings,
    permKey: 'setting',
    children: [
      { key: 'roles', label: 'Roles', path: '/settings/roles', icon: ManageAccounts },
      { key: 'users', label: 'Users', path: '/settings/users', icon: Person },
      { key: 'log-history', label: 'Log History', path: '/settings/log-history', icon: ContentPasteSearch },
    ],
  },
]

/**
 * Flatten toàn bộ nav tree thành mảng leaf paths (cho route generation).
 */
export function flattenNavItems(items: NavItem[]): NavItem[] {
  const result: NavItem[] = []
  for (const item of items) {
    if (item.children && item.children.length > 0) {
      result.push(...flattenNavItems(item.children))
    } else {
      result.push(item)
    }
  }
  return result
}
